// ============================================
// BillingDashboard Component
// ============================================
// Main billing overview showing subscription status,
// usage, and links to manage billing
// ============================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BillingDashboard.css';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import AppFooter from '../AppFooter';
import { FaHome, FaReceipt, FaCreditCard } from 'react-icons/fa';

const BillingDashboard = ({ tenantId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const actualTenantId = tenantId || params.tenant;
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, [actualTenantId]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch subscription, usage, and cost in parallel
      const [subRes, usageRes, costRes] = await Promise.all([
        axios.get(`/api/billing/subscription/${actualTenantId}`),
        axios.get(`/api/billing/usage/${actualTenantId}`),
        axios.get(`/api/billing/usage/${actualTenantId}/cost`)
      ]);

      setSubscription(subRes.data.subscription);
      setUsage(usageRes.data.usage);
      setCost(costRes.data.cost);
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError(t('billing.loadErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      const res = await axios.get(`/api/billing/portal/${actualTenantId}`);
      if (res.data.success && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Error opening portal:', err);
      alert(t('billing.portalErrorMessage'));
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(t('billing.cancelConfirmationMessage'))) {
      return;
    }

    try {
      await axios.post(`/api/billing/subscription/${actualTenantId}/cancel`);
      alert(t('billing.cancelScheduledMessage'));
      fetchBillingData();
    } catch (err) {
      console.error('Error canceling subscription:', err);
      alert(t('billing.cancelErrorMessage'));
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await axios.post(`/api/billing/subscription/${actualTenantId}/reactivate`);
      alert(t('billing.reactivateSuccessMessage'));
      fetchBillingData();
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      alert(t('billing.reactivateErrorMessage'));
    }
  };

  if (loading) {
    return (
      <div className="billing-dashboard">
        <div className="loading">{t('billing.loadingText')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-dashboard">
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="billing-dashboard">
        <div className="no-subscription">
          <h2>{t('billing.noSubscriptionTitle')}</h2>
          <p>{t('billing.setupBillingMessage')}</p>
          <button className="btn-primary">{t('billing.setupBillingButton')}</button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return '#28a745';
      case 'TRIALING': return '#ff6800';
      case 'PAST_DUE': return '#dc3545';
      case 'CANCELED': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getUsagePercentage = (current, limit, isUnlimited) => {
    if (isUnlimited) return 0;
    return Math.min(100, (current / limit) * 100);
  };

  return (
    <div className="billing-dashboard" style={{ position: 'relative', paddingBottom: '72px' }}>
      <h1>{t('billing.billingSubscriptionTitle')}</h1>

      {/* Quick Access Navigation */}
      <div className="billing-card">
        <div className="card-header">
          <h2>Quick Access</h2>
        </div>
        <div className="card-body">
          <div className="quick-access-buttons">
            <button
              className="btn-primary"
              onClick={() => navigate(`/${actualTenantId}/billing/subscription`)}
              style={{ marginRight: '10px' }}
            >
              <FaReceipt style={{ marginRight: '8px' }} />
              Manage Subscription
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate(`/${actualTenantId}/billing/payment-methods`)}
            >
              <FaCreditCard style={{ marginRight: '8px' }} />
              Payment Methods
            </button>
          </div>
        </div>
      </div>

      {/* Subscription Status Card */}
      <div className="billing-card">
        <div className="card-header">
          <h2>{t('billing.currentPlanHeader')}</h2>
          <span
            className="status-badge"
            style={{ backgroundColor: getStatusColor(subscription.status) }}
          >
            {subscription.status_display}
          </span>
        </div>
        <div className="card-body">
          <div className="plan-info">
            <div className="plan-name">{subscription.plan_name}</div>
            <div className="plan-price">
              ${subscription.price_monthly}{t('billing.pricePerMonth')}
            </div>
          </div>

          {subscription.status === 'TRIALING' && subscription.trial_days_remaining > 0 && (
            <div className="trial-info">
              <strong>{subscription.trial_days_remaining} {t('billing.trialDaysRemainingSuffix')}</strong>
              <div className="trial-end">{t('billing.trialEndsOnPrefix')}{formatDate(subscription.trial_end)}</div>
            </div>
          )}

          {subscription.cancel_at_period_end && (
            <div className="cancellation-notice">
              <strong>{t('billing.subscriptionWillCancelPrefix')}</strong>{formatDate(subscription.current_period_end)}
              <button
                className="btn-secondary"
                onClick={handleReactivateSubscription}
              >
                {t('billing.reactivateSubscriptionButton')}
              </button>
            </div>
          )}

          <div className="billing-period">
            <div>{t('billing.currentPeriodLabel')}</div>
            <div>{formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}</div>
          </div>

          <div className="action-buttons">
            <button className="btn-primary" onClick={handleOpenPortal}>
              {t('billing.manageBillingButton')}
            </button>
            {!subscription.cancel_at_period_end && (
              <button className="btn-secondary" onClick={handleCancelSubscription}>
                {t('billing.cancelSubscriptionButton')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Usage Overview Card */}
      {usage && (
        <div className="billing-card">
          <div className="card-header">
            <h2>{t('billing.currentUsageHeader')}</h2>
            <button className="btn-link" onClick={fetchBillingData}>
              {t('billing.refreshButton')}
            </button>
          </div>
          <div className="card-body">
            {/* Users */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">{t('billing.usersLabel')}</span>
                <span className="usage-count">
                  {usage.active_users}
                  {!usage.is_unlimited_users && `${t('billing.usageRatioSeparator')}${usage.included_users}`}
                  {usage.is_unlimited_users && ` ${t('billing.unlimitedIndicator')}`}
                </span>
              </div>
              {!usage.is_unlimited_users && (
                <div className="usage-bar">
                  <div
                    className="usage-fill"
                    style={{
                      width: `${getUsagePercentage(usage.active_users, usage.included_users, usage.is_unlimited_users)}%`,
                      backgroundColor: usage.users_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
              {usage.extra_users_billed > 0 && (
                <div className="extra-charge">
                  {t('billing.extraUsersChargeText', { amount: usage.extra_user_charge.toFixed(2), count: usage.extra_users_billed })}
                </div>
              )}
            </div>

            {/* Integrations */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">{t('billing.integrationsLabel')}</span>
                <span className="usage-count">
                  {usage.active_integrations}
                  {!usage.is_unlimited_integrations && `${t('billing.usageRatioSeparator')}${usage.included_integrations}`}
                  {usage.is_unlimited_integrations && ` ${t('billing.unlimitedIndicator')}`}
                </span>
              </div>
              {!usage.is_unlimited_integrations && (
                <div className="usage-bar">
                  <div
                    className="usage-fill"
                    style={{
                      width: `${getUsagePercentage(usage.active_integrations, usage.included_integrations, usage.is_unlimited_integrations)}%`,
                      backgroundColor: usage.integrations_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Scheduled Posts */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">{t('billing.scheduledPostsLabel')}</span>
                <span className="usage-count">
                  {usage.scheduled_posts_count}
                  {!usage.is_unlimited_posts && `${t('billing.usageRatioSeparator')}${usage.included_posts_monthly}`}
                  {usage.is_unlimited_posts && ` ${t('billing.unlimitedIndicator')}`}
                </span>
              </div>
              {!usage.is_unlimited_posts && (
                <div className="usage-bar">
                  <div
                    className="usage-fill"
                    style={{
                      width: `${getUsagePercentage(usage.scheduled_posts_count, usage.included_posts_monthly, usage.is_unlimited_posts)}%`,
                      backgroundColor: usage.posts_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cost Estimate Card */}
      {cost && (
        <div className="billing-card">
          <div className="card-header">
            <h2>{t('billing.estimatedCostHeader')}</h2>
          </div>
          <div className="card-body">
            <div className="cost-breakdown">
              <div className="cost-item">
                <span>{t('billing.basePlanLabel', { planName: cost.planName })}</span>
                <span>${cost.baseCost.toFixed(2)}</span>
              </div>
              {cost.extraUsers > 0 && (
                <div className="cost-item">
                  <span>{t('billing.extraUsersLabel', { count: cost.extraUsers })}</span>
                  <span>${cost.extraUserCost.toFixed(2)}</span>
                </div>
              )}
              <div className="cost-total">
                <span>{t('billing.totalLabel')}</span>
                <span>${cost.totalCost.toFixed(2)}{t('billing.currencySeparator')}{cost.currency.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* App Footer */}
      <AppFooter
        buttons={[
          {
            icon: <FaReceipt />,
            label: "Subscription",
            onClick: () => navigate(`/${actualTenantId}/billing/subscription`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${actualTenantId}/dashboard`),
          },
          {
            icon: <FaCreditCard />,
            label: "Payment",
            onClick: () => navigate(`/${actualTenantId}/billing/payment-methods`),
          },
        ]}
      />
    </div>
  );
};

export default BillingDashboard;
